import { Card } from 'react-bootstrap';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { monserratSemiBold } from '@habitech/shared';
import { useSelectedWorkflowStep } from '../../../../hooks/useSelectedWorkflowStep';
import {
  getOrchestratorNid,
  getSelectedResponse,
  getSelectedStep,
  setSelectedResponse,
} from '../../../../store/slices/domainOrchestratorPricingCommittee.slice';
import { CircleTracker } from '../../../OrchestratorTracker/components/OrchestratorTrackerLine/components/TrackerItems/CircleTracker';
import { LineTracker } from '../../../OrchestratorTracker/components/OrchestratorTrackerLine/components/TrackerItems/LineTracker';
import { StepInfoTracker } from './components/TrackerItems/StepInfoTracker';
import { DocumentsModalViewer } from '@pages/DocumentAuditPage/modules/DocumentTaskDetail/components/DocumentsViewer/DocumentsViewer';
import { DocumentsHistorySection } from './components/DocumentsHistorySection';

export const StepTrackerLine = () => {
  const dispatch = useDispatch();
  const nid = useSelector(getOrchestratorNid);
  const selectedResponse = useSelector(getSelectedResponse);
  const selectedStep = useSelector(getSelectedStep);
  const { step } = useSelectedWorkflowStep();

  const isApproverStep = selectedStep === 'idm aprobologia';
  const isDocumentsReviewStep = selectedStep === 'idm revision de documentos';

  useEffect(() => {
    if (!step || step.responses.length === 0) {
      dispatch(setSelectedResponse(-1));
      return;
    }
    dispatch(setSelectedResponse(step.responses.length - 1));
  }, [dispatch, step]);

  if (!step || step.responses.length === 0) {
    return <div className="text-muted">Sin historial</div>;
  }

  return (
    <div className="d-flex" style={{ height: '100%' }}>
      <div
        className="d-flex flex-column position-relative pt-1 me-3"
        style={{
          height: '100%',
          overflowY: 'auto',
          paddingRight: '1rem',
        }}
      >
        {[...step.responses]
          .map((r, idx, arr) => arr[arr.length - 1 - idx])
          .map((res, index) => {
            const actualIndex = step.responses.length - 1 - index;
            const isLast = actualIndex === step.responses.length - 1;
            const colorStatus = isLast
              ? (
                  {
                    COMPLETED: 'purple',
                    IN_PROGRESS: 'orange',
                    REJECTED: 'red',
                  } as Record<string, string>
                )[res.status] || '#6c757d'
              : '#6c757d';

            const item = (
              <div
                className="d-flex align-items-start"
                data-testid={`response-${index}`}
                onClick={() => dispatch(setSelectedResponse(actualIndex))}
                style={{ cursor: 'pointer' }}
                aria-hidden="true"
              >
                <CircleTracker colorStatus={colorStatus} />
                <StepInfoTracker
                  status={res.status}
                  date={res.created_at}
                  colorStatus={colorStatus}
                />
              </div>
            );

            const isSelected = selectedResponse === actualIndex;

            return (
              <div key={res.created_at} className="mb-4">
                {isSelected ? (
                  <Card data-testid={`card-response-${index}`}>
                    <Card.Body className="p-2">{item}</Card.Body>
                  </Card>
                ) : (
                  item
                )}
                <LineTracker
                  colorStatus={colorStatus}
                  position={index}
                  steps={step.responses.map((_, i) => i.toString())}
                />
              </div>
            );
          })}
      </div>
      {selectedResponse > -1 && step.responses[selectedResponse] && (
        <div
          className="flex-grow-1"
          style={{
            height: '100%',
            overflowY: 'auto',
            paddingRight: '1rem',
          }}
        >
          {step.responses[selectedResponse].retry_all && (
            <div className="mb-1">
              <strong>Retry All:</strong>{' '}
              {String(step.responses[selectedResponse].retry_all)}
            </div>
          )}
          <div className="d-flex flex-column gap-4">
            {step.responses[selectedResponse].reason_for_discard?.label && (
              <strong>
                Razón de descarte:{' '}
                {step.responses[selectedResponse].reason_for_discard.label}
              </strong>
            )}
            {step.responses[selectedResponse].detail != null && (
              <div className="d-flex flex-column">
                {(step.responses[selectedResponse].detail as any)
                  .owner_process && (
                  <div className="d-flex gap-2 align-items-center flex-column">
                    <div
                      className="d-flex gap-2 align-items-center w-100"
                      data-testid="owner-process"
                    >
                      <div
                        className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center me-2"
                        style={{ width: '1.5rem', height: '1.5rem' }}
                      >
                        {String(
                          (step.responses[selectedResponse].detail as any)
                            .owner_process
                        )
                          .charAt(0)
                          .toUpperCase()}
                      </div>
                      <span>
                        {
                          (step.responses[selectedResponse].detail as any)
                            .owner_process
                        }
                      </span>
                    </div>
                    <div
                      style={{
                        maxWidth: '600px',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                      }}
                    >
                      {step.responses[selectedResponse].comment && (
                        <div className="mb-2">
                          <strong>Comentario:</strong>{' '}
                          {step.responses[selectedResponse].comment}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <pre className="bg-light p-2">
                  {JSON.stringify(
                    step.responses[selectedResponse].detail,
                    null,
                    2
                  ) || ''}
                </pre>
              </div>
            )}
          </div>
          {step.responses[selectedResponse].retry_policy && (
            <div className="mb-1">
              <strong>Retry Policy:</strong>{' '}
              {step.responses[selectedResponse].retry_policy}
            </div>
          )}
          {step.responses[selectedResponse].next_steps.length > 0 && (
            <div className="mb-1">
              <strong>Siguientes pasos:</strong>
              {step.responses[selectedResponse].next_steps.join(', ')}
            </div>
          )}

          {/* Sección de Historial de Revisiones - Solo en paso de revisión de documentos */}
          {isDocumentsReviewStep && <DocumentsHistorySection nid={nid} />}

          {isApproverStep && (
            <div className="d-flex flex-column">
              <div
                className="fw-bold px-4 pb-2 fs-6"
                style={{ fontFamily: monserratSemiBold }}
              >
                Documentos
              </div>
              <DocumentsModalViewer nid={nid} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
